import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, forkJoin } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { Review, ReviewStatus } from '@app/shared/models/review.model';
import { FetchState, PatchState } from '@app/shared/models/fetch-state.model';
import { HttpService } from '@app/shared/services/http';
import { FhirFacadeService } from '@app/shared/services/fhir-facade';
import { OrganizationResource } from '@app/shared/models/facade-response.model';

export interface HospitalIdentity {
  name: string;
  alias: string | null;
}
type ReviewDraft = Readonly<{ status: ReviewStatus }>;
type SuccessItem = Readonly<{
  id: string;
  ok: true;
  res: { message: string };
}>;
type ErrorItem = Readonly<{
  id: string;
  ok: false;
  err: unknown;
}>;
type BatchItem = SuccessItem | ErrorItem;

@Injectable({ providedIn: 'root' })
export class ReviewService {
  private readonly baseUrl = `${environment.apiUrl}Review`;

  private readonly reviewStateSubject = new BehaviorSubject<
    FetchState<Review[]>
  >({ status: 'loading' });
  readonly reviewState$ = this.reviewStateSubject.asObservable();

  private readonly patchStateSubject = new BehaviorSubject<PatchState>({
    status: 'idle',
  });
  readonly patchState$ = this.patchStateSubject.asObservable();

  private readonly reviewDrafts = new Map<string, ReviewDraft>();

  private readonly hospitalIdentityCache = new Map<string, HospitalIdentity>();

  private readonly pendingIdentityRequests = new Map<
    string,
    Observable<[string, HospitalIdentity]>
  >();

  constructor(
    private readonly httpService: HttpService,
    private readonly fhirFacade: FhirFacadeService,
  ) {}

  isInitialLoad(): boolean {
    return this.reviewStateSubject.getValue().status === 'loading';
  }

  fetchReviews(): void {
    this.reviewStateSubject.next({ status: 'loading' });

    this.httpService.get<{ items: Review[] }>(this.baseUrl).subscribe({
      next: (response) =>
        this.reviewStateSubject.next({
          status: 'success',
          data: response.items,
        }),
      error: (error) =>
        this.reviewStateSubject.next({ status: 'error', error }),
    });
  }

  patchReviewsBatch(patches: Partial<Review>[]): void {
    const validPatches = (patches ?? []).filter(
      (p): p is Partial<Review> & { id: string } => !!p?.id,
    );
    if (validPatches.length === 0) return;

    this.patchStateSubject.next({ status: 'saving' });

    const requests = validPatches.map((p) =>
      this.httpService
        .patch<{ message: string }>(`${this.baseUrl}/${String(p.id)}`, p)
        .pipe(
          map((res): SuccessItem => ({ id: String(p.id), ok: true, res })),
          catchError(
            (err): Observable<ErrorItem> =>
              of({ id: String(p.id), ok: false, err }),
          ),
        ),
    );

    forkJoin(requests).subscribe({
      next: (results: BatchItem[]) => {
        const succeeded = results.filter((r): r is SuccessItem => r.ok);
        const failed = results.filter((r): r is ErrorItem => !r.ok);

        // 成功したIDのドラフトのみ削除
        for (const { id } of succeeded) this.reviewDrafts.delete(id);

        if (succeeded.length > 0) {
          this.fetchReviews();
        }

        if (failed.length > 0) {
          const failedIds = failed.map((r) => r.id);
          const isAllFailed = failed.length === results.length;

          const error = new HttpErrorResponse({
            error: { failedIds },
            status: isAllFailed ? 400 : 207,
            statusText: isAllFailed ? 'Bad Request' : 'Partial Failure',
          });

          this.patchStateSubject.next({ status: 'error', error });
          return;
        }

        this.patchStateSubject.next({
          status: 'success',
          data: {
            message: `レビュー状況を ${succeeded.length} 件更新しました。`,
          },
        });
      },
      error: (error) => this.patchStateSubject.next({ status: 'error', error }),
    });
  }

  resetPatchState(): void {
    this.patchStateSubject.next({ status: 'idle' });
  }

  saveDraft(id: string, draft: ReviewDraft): void {
    if (!id) return;
    this.reviewDrafts.set(id, { status: draft.status });
  }

  getDraft(id: string): ReviewDraft | undefined {
    return this.reviewDrafts.get(id);
  }

  getDrafts(): Map<string, ReviewDraft> {
    return this.reviewDrafts;
  }

  removeDraft(id: string): void {
    this.reviewDrafts.delete(id);
  }

  clearDrafts(): void {
    this.reviewDrafts.clear();
  }

  // 存在しない分だけ病院名称を取得してキャッシュに追加
  resolveHospitalIdentities(
    hospitalCodes: string[],
  ): Observable<Record<string, HospitalIdentity>> {
    const uniqueCodes = this.uniqueStrings(hospitalCodes);
    if (uniqueCodes.length === 0) return of({});

    const missingCodes = uniqueCodes.filter(
      (code) => !this.hospitalIdentityCache.has(code),
    );
    const requests: Observable<[string, HospitalIdentity]>[] = [];

    for (const code of missingCodes) {
      const inflight$ = this.pendingIdentityRequests.get(code);
      if (inflight$) {
        requests.push(inflight$);
        continue;
      }

      const request$ = this.fhirFacade.fetchFacadeOrganization(code).pipe(
        map(
          (org) =>
            [code, this.toHospitalIdentity(org)] as [string, HospitalIdentity],
        ),
        catchError(() =>
          of<[string, HospitalIdentity]>([code, { name: code, alias: null }]),
        ),
        shareReplay(1),
        finalize(() => this.pendingIdentityRequests.delete(code)),
      );

      this.pendingIdentityRequests.set(code, request$);
      requests.push(request$);
    }

    const joined$ = requests.length
      ? forkJoin(requests)
      : of<[string, HospitalIdentity][]>([]);
    return joined$.pipe(
      tap((entries) => {
        for (const [code, entry] of entries) {
          this.hospitalIdentityCache.set(code, entry);
        }
      }),
      map(() => {
        const result: Record<string, HospitalIdentity> = {};
        for (const code of uniqueCodes) {
          const entry = this.hospitalIdentityCache.get(code);
          if (entry) result[code] = entry;
        }
        return result;
      }),
    );
  }

  private toHospitalIdentity(org: OrganizationResource): HospitalIdentity {
    const alias =
      Array.isArray(org.alias) && org.alias.length > 0 ? org.alias[0] : null;
    return { name: org.name, alias };
  }

  private uniqueStrings(values: unknown[]): string[] {
    return Array.from(
      new Set(
        (values ?? []).filter(
          (v): v is string => typeof v === 'string' && v.trim().length > 0,
        ),
      ),
    );
  }
}
